import { Schema } from 'prosemirror-model'
import { schema as basicSchema } from 'prosemirror-schema-basic'
import { addListNodes } from 'prosemirror-schema-list'
import { tableNodes } from 'prosemirror-tables'
import { kAgentV2SourceUnavailableLabel, kAgentV2UnverifiedSourceLabel } from '@/agent-v2/references/agent-v2-citation-labels'
import { kCitationIdRegex } from '@/util/agent-citations'
import { kPleadingCaptionNodeName, pleadingCaptionNodeSpec } from '../custom_markdown_blocks/pleading-caption'
import {
  kArtifactLegalCitationCanOpenAttr,
  kArtifactLegalCitationIdAttr,
  kArtifactLegalCitationResolvedAttr,
  kArtifactReferenceMarkerCanOpenAttr,
  kArtifactReferenceMarkerIdAttr,
  kArtifactReferenceMarkerLabelAttr,
  kArtifactReferenceMarkerResolvedAttr,
} from '../utils/artifact-citation-helpers'

/**
 * ProseMirror schema for the Markdown canvas.
 */
export const markdownEditorSchema = new Schema({
  nodes: addListNodes(basicSchema.spec.nodes, 'paragraph block*', 'block')
    .append(
      tableNodes({
        tableGroup: 'block',
        cellContent: 'block+',
        cellAttributes: {
          background: {
            default: null,
            getFromDOM(dom) {
              return (dom as HTMLElement).style.backgroundColor || null
            },
            setDOMAttr(value, attrs) {
              if (value) attrs.style = (attrs.style || '') + `background-color: ${value};`
            },
          },
        },
      })
    )
    .addToEnd(kPleadingCaptionNodeName, pleadingCaptionNodeSpec)
    .addToEnd('referenceMarker', {
      group: 'inline',
      inline: true,
      content: 'text*',
      selectable: false,
      attrs: {
        citationId: { default: '' },
        label: { default: '?' },
        isResolved: { default: false },
        canOpen: { default: false },
      },
      parseDOM: [
        {
          tag: `span[${kArtifactReferenceMarkerIdAttr}]`,
          getAttrs(dom) {
            const element = dom as HTMLElement
            const citationId = readCitationId(element, kArtifactReferenceMarkerIdAttr)
            if (!citationId) return false

            return {
              citationId,
              label: readCitationLabel(element.getAttribute(kArtifactReferenceMarkerLabelAttr)),
              isResolved: element.getAttribute(kArtifactReferenceMarkerResolvedAttr) === 'true',
              canOpen: element.getAttribute(kArtifactReferenceMarkerCanOpenAttr) === 'true',
            }
          },
        },
      ],
      toDOM(node) {
        return [
          'span',
          {
            [kArtifactReferenceMarkerIdAttr]: node.attrs.citationId,
            [kArtifactReferenceMarkerLabelAttr]: node.attrs.label,
            [kArtifactReferenceMarkerResolvedAttr]: String(node.attrs.isResolved),
            [kArtifactReferenceMarkerCanOpenAttr]: String(node.attrs.canOpen),
            class: 'reference-marker-badge',
          },
          `[${node.attrs.citationId}]`,
        ]
      },
    }),
  marks: basicSchema.spec.marks.addBefore('link', 'legalCitation', {
    attrs: {
      citationId: { default: '' },
      isResolved: { default: false },
      canOpen: { default: false },
    },
    inclusive: false,
    parseDOM: [
      {
        tag: `span[${kArtifactLegalCitationIdAttr}]`,
        getAttrs(dom) {
          const element = dom as HTMLElement
          const citationId = readCitationId(element, kArtifactLegalCitationIdAttr)
          if (!citationId) return false

          return {
            citationId,
            isResolved: element.getAttribute(kArtifactLegalCitationResolvedAttr) === 'true',
            canOpen: element.getAttribute(kArtifactLegalCitationCanOpenAttr) === 'true',
          }
        },
      },
    ],
    toDOM(mark) {
      const isResolved = Boolean(mark.attrs.isResolved)
      const canOpen = Boolean(mark.attrs.canOpen)
      return [
        'span',
        {
          [kArtifactLegalCitationIdAttr]: mark.attrs.citationId,
          [kArtifactLegalCitationResolvedAttr]: String(isResolved),
          [kArtifactLegalCitationCanOpenAttr]: String(canOpen),
          title: !isResolved ? kAgentV2UnverifiedSourceLabel : canOpen ? 'Open source' : kAgentV2SourceUnavailableLabel,
          'aria-disabled': canOpen ? undefined : 'true',
          class: !isResolved
            ? 'legal-citation-link text-brand-neutral-600 underline decoration-brand-neutral-600/40 underline-offset-2'
            : canOpen
            ? 'legal-citation-link text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:text-brand-800 hover:decoration-brand-800 cursor-pointer'
            : 'legal-citation-link text-brand-neutral-600 underline decoration-brand-neutral-600/40 underline-offset-2 cursor-not-allowed',
        },
        0,
      ]
    },
  }),
})

function readCitationId(element: HTMLElement, attributeName: string): string {
  const citationId = element.getAttribute(attributeName) ?? ''
  return kCitationIdRegex.test(citationId) ? citationId : ''
}

function readCitationLabel(label: string | null): string {
  return label === '?' || /^\d+(?:\.\d+)?$/.test(label ?? '') ? label ?? '?' : '?'
}
